import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, BookOpen, Lightbulb, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        navigate("/dashboard");
      }
      
      if (event === 'USER_UPDATED') {
        toast({
          title: "Success",
          description: "Please check your email to confirm your account",
        });
      }
      
      if (event === 'PASSWORD_RECOVERY') {
        toast({
          title: "Password Reset",
          description: "Check your email for the password reset link",
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="w-full max-w-6xl flex gap-8 items-center relative z-10">
        {/* Left side - Branding & Features */}
        <div className="hidden lg:flex flex-1 flex-col gap-8 animate-fade-in">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-medium text-sm">
              <Sparkles className="h-4 w-4" />
              <span>Powered by AI</span>
            </div>
            <h1 className="text-6xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent leading-tight">
              LinkedIn Content AI
            </h1>
            <p className="text-xl text-muted-foreground max-w-lg">
              Transform your LinkedIn presence with AI-powered content strategy and engagement management
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 card-hover">
              <div className="p-3 rounded-lg bg-primary/10">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Knowledge Base</h3>
                <p className="text-sm text-muted-foreground">
                  Upload documents, videos, and blogs to build your content library
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 card-hover" style={{ animationDelay: '0.1s' }}>
              <div className="p-3 rounded-lg bg-primary/10">
                <Lightbulb className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Content Strategist</h3>
                <p className="text-sm text-muted-foreground">
                  Generate strategies with 20 unique post ideas, avoiding duplicates
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 card-hover" style={{ animationDelay: '0.2s' }}>
              <div className="p-3 rounded-lg bg-primary/10">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Engagement Manager</h3>
                <p className="text-sm text-muted-foreground">
                  AI-powered comments that build authority and drive engagement
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth form */}
        <div className="w-full lg:w-[440px] animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="text-center mb-8 lg:hidden">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent mb-2">
              LinkedIn Content AI
            </h1>
            <p className="text-muted-foreground">Sign in to get started</p>
          </div>

          <div className="glass-effect p-8 rounded-2xl shadow-2xl">
            <div className="mb-6 text-center lg:text-left">
              <h2 className="text-2xl font-bold mb-2">Welcome</h2>
              <p className="text-muted-foreground">Sign in or create an account to continue</p>
            </div>
            
            <SupabaseAuth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                variables: {
                  default: {
                    colors: {
                      brand: 'hsl(262 83% 58%)',
                      brandAccent: 'hsl(280 70% 65%)',
                    },
                  },
                },
                className: {
                  container: 'space-y-4',
                  button: 'w-full px-4 py-2.5 rounded-lg font-medium transition-all hover:scale-[1.02]',
                  input: 'w-full px-4 py-2.5 rounded-lg border border-input bg-background transition-all focus:ring-2 focus:ring-primary/20',
                  label: 'text-sm font-medium text-foreground mb-2 block',
                },
              }}
              theme="default"
              providers={[]}
              redirectTo={`${window.location.origin}/dashboard`}
              localization={{
                variables: {
                  sign_up: {
                    email_label: 'Email address',
                    password_label: 'Create a password',
                    email_input_placeholder: 'Your email address',
                    password_input_placeholder: 'Your password',
                    button_label: 'Sign up',
                    loading_button_label: 'Signing up...',
                    social_provider_text: 'Sign up with {{provider}}',
                    link_text: "Don't have an account? Sign up",
                    confirmation_text: 'Check your email for the confirmation link'
                  },
                  sign_in: {
                    email_label: 'Email address',
                    password_label: 'Your password',
                    email_input_placeholder: 'Your email address',
                    password_input_placeholder: 'Your password',
                    button_label: 'Sign in',
                    loading_button_label: 'Signing in...',
                    social_provider_text: 'Sign in with {{provider}}',
                    link_text: 'Already have an account? Sign in'
                  },
                },
              }}
            />
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
